/**
 * useModalProps Hook
 * Phase 1 Days 46-48 - Modal prop assembly extraction
 *
 * Assembles all props for ModalsContainer component.
 * This reduces the size of AppContent.js render section significantly.
 */

import { useMemo } from "react";
import config from "../config";

export function useModalProps({
  // Display Name Modal
  isEditingDisplayName,
  editingDisplayNameValue,
  deviceStates,
  selectedDeviceId,
  isSavingDisplayName,
  handleDisplayNameInputChange,
  handleDisplayNameKeyDown,
  handleCancelEditDisplayName,
  handleSaveDisplayName,

  // WebSocket Modal
  isModalEnabled,
  isAutoReconnectEnabled,
  connectionStatus,
  connectWebSocketStore,

  // Alert Modals
  forcedEntryAlert,
  clearForcedEntryAlert,
  proppedDoorAlert,
  clearProppedDoorAlert,
  eventDeniedAlert,
  clearEventDeniedAlert,

  // Advanced Settings Modal
  isAdvancedSettingsOpen,
  organizationId,
  organizationName,
  user,
  handleSettingsUpdated,
  closeAdvancedSettings,

  // Device Claiming Modal
  isDeviceClaimingModalOpen,
  devices,
  handleRefresh,
  setIsDeviceClaimingModalOpen,

  // Delete Credential Modal
  deleteCredentialModal,
  handleConfirmDeleteCredential,
  handleCancelDeleteCredential,

  // Installation Actions Modal
  isInstallationActionsOpen,
  eligibleForLockdown,
  eligibleForRelease,
  eligibleDevicesForLockdown,
  eligibleDevicesForRelease,
  installationName,
  allDeviceStatesLoaded,
  handleBulkLockdownAction,
  handleRetryInstallationSync,
  setIsInstallationActionsOpen,
  handleOpenInstallationManagement,

  // Installation Management Modal
  isInstallationManagementOpen,
  managedOrganizationId,
  orgInstallContext,
  handleInstallationsChanged,
  handleSwitchInstallation,
  setInstallationManagementOpen,

  // Organization Management Modal
  isOrganizationManagementOpen,
  handleOrganizationsChanged,
  handleSwitchOrganization,
  closeOrganizationManagementModal,

  // Bulk Action Status Modal
  isBulkStatusModalOpen,
  bulkActionResults,
  bulkActionStatuses,
  bulkActionTimestamps,
  bulkActionType,
  setBulkStatusModalOpen,
  clearBulkActionResults,
  clearBulkActionStatuses,
  clearBulkActionTimestamps,

  // Password Modal
  showPasswordModal,
  closePasswordModal,
  handlePasswordConfirm,
  passwordModalPurpose,

  // Common
  theme,
  getAccessToken,

  // Device Update Modal (firmware/WiFi)
  authenticatedFetch,
  installationId,

  // Credentials Setting Modal (7 individual params — avoids useMemo re-computation on object ref change)
  isCredentialSettingModalOpen,
  credentialSettingKey,
  credentialSettingLabel,
  credentialSettingCurrentValue,
  credentialSettingConfig,
  isCredentialSettingSaving,
  handleSaveCredentialSetting,
  handleCancelCredentialSettingEdit,
}) {
  return useMemo(
    () => ({
      // Edit Display Name Modal
      isEditingDisplayName,
      editedDisplayName: editingDisplayNameValue,
      currentDisplayName: deviceStates[selectedDeviceId]?.displayName,
      isSavingDisplayName,
      onDisplayNameChange: handleDisplayNameInputChange,
      onDisplayNameKeyDown: handleDisplayNameKeyDown,
      onCancelDisplayNameEdit: handleCancelEditDisplayName,
      onSaveDisplayName: handleSaveDisplayName,

      // WebSocket Reconnect Modal
      showWsReconnectModal: isModalEnabled && !isAutoReconnectEnabled,
      connectionStatus,
      onReconnect: () => connectWebSocketStore(config.websocketUrl),

      // Alert Modals
      forcedEntryDevice: forcedEntryAlert
        ? {
            deviceId: forcedEntryAlert.deviceId,
            displayName: deviceStates[forcedEntryAlert?.deviceId]?.displayName,
            timestamp: forcedEntryAlert.timestamp,
          }
        : null,
      onCloseForcedEntryAlert: clearForcedEntryAlert,

      proppedDoorDevice: proppedDoorAlert
        ? {
            deviceId: proppedDoorAlert.deviceId,
            displayName: deviceStates[proppedDoorAlert?.deviceId]?.displayName,
            timestamp: proppedDoorAlert.timestamp,
          }
        : null,
      onCloseProppedDoorAlert: clearProppedDoorAlert,

      denyEventData: eventDeniedAlert
        ? {
            deviceId: eventDeniedAlert.deviceId,
            displayName: deviceStates[eventDeniedAlert?.deviceId]?.displayName,
            timestamp: eventDeniedAlert.timestamp,
            actionType: eventDeniedAlert.actionType,
            actionDetails: eventDeniedAlert.actionDetails,
          }
        : null,
      onCloseDenyEventModal: clearEventDeniedAlert,

      // Advanced Settings Modal
      showAdvancedSettingsModal: isAdvancedSettingsOpen,
      orgId: organizationId,
      organizationName,
      userRole: user?.roles?.[0],
      onSettingsUpdated: handleSettingsUpdated,
      onCloseAdvancedSettingsModal: closeAdvancedSettings,

      // Device Claiming Modal
      showDeviceClaimingModal: isDeviceClaimingModalOpen,
      devices,
      onDevicesClaimed: handleRefresh,
      onCloseDeviceClaimingModal: () => setIsDeviceClaimingModalOpen(false),

      // Delete Credential Confirm Modal
      showDeleteConfirmModal: deleteCredentialModal.isOpen,
      credentialId: deleteCredentialModal.credentialId,
      patronName: deleteCredentialModal.patronName,
      deviceName:
        devices.find((d) => d.deviceId === selectedDeviceId)?.displayName ||
        "this device",
      isDeleting: deleteCredentialModal.isDeleting,
      onConfirmDeleteCredential: handleConfirmDeleteCredential,
      onCancelDeleteCredential: handleCancelDeleteCredential,

      // Installation Actions Modal
      showInstallActionsModal: isInstallationActionsOpen,
      deviceCount: devices?.length || 0,
      eligibleForLockdown,
      eligibleForRelease,
      eligibleDevicesForLockdown,
      eligibleDevicesForRelease,
      installationName: installationName || "Unknown",
      allDeviceStatesLoaded,
      onExecuteAction: handleBulkLockdownAction,
      onRetrySync: handleRetryInstallationSync,
      onCloseInstallActionsModal: () => setIsInstallationActionsOpen(false),
      onManageInstallation: handleOpenInstallationManagement,

      // Installation Management Modal
      showInstallManagementModal: isInstallationManagementOpen,
      installations: managedOrganizationId
        ? (orgInstallContext?.organizations || []).find(
            (org) => org.orgId === managedOrganizationId,
          )?.installations || []
        : orgInstallContext?.installations || [],
      currentInstallationId: orgInstallContext?.installationId,
      organizationId:
        managedOrganizationId || orgInstallContext?.organizationId,
      onInstallationsChanged: handleInstallationsChanged,
      onSwitchInstallation: handleSwitchInstallation,
      onChangeInstallation: orgInstallContext?.changeInstallation,
      devicesForInstallManagement: devices || [],
      onCloseInstallManagementModal: () =>
        setInstallationManagementOpen(false, null),

      // Organization Management Modal
      showOrgManagementModal: isOrganizationManagementOpen,
      organizations: orgInstallContext?.organizations || [],
      currentOrganizationId: orgInstallContext?.organizationId,
      onOrganizationsChanged: handleOrganizationsChanged,
      onSwitchOrganization: handleSwitchOrganization,
      onChangeOrganization: orgInstallContext?.changeOrganization,
      onCloseOrgManagementModal: closeOrganizationManagementModal,
      onOpenInstallationManagement: (organizationId) => {
        closeOrganizationManagementModal();
        handleOpenInstallationManagement(organizationId);
      },

      // Common props for multiple modals
      theme,
      userProfile: user,
      getAccessToken,

      // Bulk Action Status Modal
      showBulkStatusModal: isBulkStatusModalOpen,
      bulkActionResults,
      bulkActionStatuses,
      deviceStates,
      bulkActionType,
      bulkInstallationName: installationName || "Unknown",
      onCloseBulkStatusModal: () => {
        setBulkStatusModalOpen(false);
        clearBulkActionResults();
        clearBulkActionStatuses();
      },

      // Device Update Modal (firmware/WiFi change notification)
      authenticatedFetch,
      installationId: orgInstallContext?.installationId || installationId,

      // Credentials Setting Modal
      credentialsSettingModal: {
        isOpen: isCredentialSettingModalOpen,
        settingKey: credentialSettingKey,
        settingLabel: credentialSettingLabel,
        currentValue: credentialSettingCurrentValue,
        settingConfig: credentialSettingConfig,
        isSaving: isCredentialSettingSaving,
        onSave: handleSaveCredentialSetting,
        onCancel: handleCancelCredentialSettingEdit,
      },

      // Password Prompt Modal
      showPasswordModal,
      passwordModalTitle:
        passwordModalPurpose === "firmware"
          ? "Update Device Firmware"
          : passwordModalPurpose === "wifi"
          ? "⚠️ Change Wi-Fi Enabled Status"
          : "Download Complete Device Settings",
      passwordModalMessage:
        passwordModalPurpose === "firmware"
          ? "This will initiate a firmware update on the selected device. Enter Special Operator password to continue (only the Demo Support Team has this):"
          : passwordModalPurpose === "wifi"
          ? "WARNING: Disabling Wi-Fi will make this lock completely unreachable over the network. It can only be re-enabled via Bluetooth (BLE) by a technician on-site. Enter Special Operator password to confirm:"
          : "This will download complete device configuration data. Enter Special Operator password to continue (only the Demo Support Team has this):",
      onClosePasswordModal: closePasswordModal,
      onPasswordConfirm: handlePasswordConfirm,
    }),
    [
      isEditingDisplayName,
      editingDisplayNameValue,
      deviceStates,
      selectedDeviceId,
      isSavingDisplayName,
      handleDisplayNameInputChange,
      handleDisplayNameKeyDown,
      handleCancelEditDisplayName,
      handleSaveDisplayName,
      isModalEnabled,
      isAutoReconnectEnabled,
      connectionStatus,
      connectWebSocketStore,
      forcedEntryAlert,
      clearForcedEntryAlert,
      proppedDoorAlert,
      clearProppedDoorAlert,
      eventDeniedAlert,
      clearEventDeniedAlert,
      isAdvancedSettingsOpen,
      organizationId,
      organizationName,
      user,
      handleSettingsUpdated,
      closeAdvancedSettings,
      isDeviceClaimingModalOpen,
      devices,
      handleRefresh,
      setIsDeviceClaimingModalOpen,
      deleteCredentialModal,
      handleConfirmDeleteCredential,
      handleCancelDeleteCredential,
      isInstallationActionsOpen,
      eligibleForLockdown,
      eligibleForRelease,
      eligibleDevicesForLockdown,
      eligibleDevicesForRelease,
      installationName,
      allDeviceStatesLoaded,
      handleBulkLockdownAction,
      handleRetryInstallationSync,
      setIsInstallationActionsOpen,
      handleOpenInstallationManagement,
      isInstallationManagementOpen,
      managedOrganizationId,
      orgInstallContext,
      handleInstallationsChanged,
      handleSwitchInstallation,
      setInstallationManagementOpen,
      isOrganizationManagementOpen,
      handleOrganizationsChanged,
      handleSwitchOrganization,
      closeOrganizationManagementModal,
      theme,
      getAccessToken,
      isBulkStatusModalOpen,
      bulkActionResults,
      bulkActionStatuses,
      bulkActionType,
      setBulkStatusModalOpen,
      clearBulkActionResults,
      clearBulkActionStatuses,
      showPasswordModal,
      closePasswordModal,
      handlePasswordConfirm,
      passwordModalPurpose,
      authenticatedFetch,
      installationId,
      isCredentialSettingModalOpen,
      credentialSettingKey,
      credentialSettingLabel,
      credentialSettingCurrentValue,
      credentialSettingConfig,
      isCredentialSettingSaving,
      handleSaveCredentialSetting,
      handleCancelCredentialSettingEdit,
    ],
  );
}
